/**
 * Sales money (docs/sales.spec.md, "Money is exact"): integer cents throughout,
 * and one rounding rule, so the API, the web app and the emails always agree to
 * the cent. Percentages are kept as thousandths of a percent: 8.875% is 8875,
 * 100% is 100_000.
 */

/** Thousandths of a percent. */
export type Milli = number

const MILLI_PER_PERCENT = 1000
export const MAX_MILLI = 100_000
/** The largest bank-transfer discount a venue may set: 10%. */
export const MAX_BANK_DISCOUNT_MILLI = 10_000

/** `cents × milli / 100_000`, rounded half up to the cent. The one rounding rule. */
export function percentOf(cents: number, milli: Milli): number {
  return Math.round((cents * milli) / MAX_MILLI)
}

/** "8.875" → 8875; null for anything that isn't a percentage with at most three decimals. */
export function percentToMilli(text: string): Milli | null {
  const match = /^\s*(\d{1,3})(?:\.(\d{1,3}))?\s*%?\s*$/.exec(text)
  if (match === null) return null
  const whole = Number(match[1])
  const fraction = Number((match[2] ?? '').padEnd(3, '0'))
  const milli = whole * MILLI_PER_PERCENT + fraction
  return milli > MAX_MILLI ? null : milli
}

/** 8875 → "8.875"; 7000 → "7". */
export function milliToPercent(milli: Milli): string {
  const whole = Math.trunc(milli / MILLI_PER_PERCENT)
  const fraction = String(milli % MILLI_PER_PERCENT).padStart(3, '0').replace(/0+$/, '')
  return fraction === '' ? String(whole) : `${String(whole)}.${fraction}`
}

export interface QuoteLine {
  key: string
  unitCents: number
  quantity: number
  taxable: boolean
  serviceCharged: boolean
}

export interface QuoteRates {
  taxMilli: Milli
  serviceChargeMilli: Milli
  serviceChargeTaxed: boolean
}

export interface Quote {
  lines: { key: string; totalCents: number }[]
  subtotalCents: number
  serviceChargeCents: number
  taxCents: number
  totalCents: number
}

/**
 * What a set of lines comes to. The service charge is on the lines marked for
 * it; tax is on the taxable lines, and on the service charge when the venue
 * taxes it. Each is rounded once, on its whole base, never line by line.
 */
export function quote(lines: readonly QuoteLine[], rates: QuoteRates): Quote {
  const totals = lines.map((line) => ({ line, totalCents: line.unitCents * line.quantity }))
  const sum = (pick: (l: QuoteLine) => boolean): number => totals.reduce((a, t) => a + (pick(t.line) ? t.totalCents : 0), 0)
  const subtotalCents = sum(() => true)
  const serviceChargeCents = percentOf(sum((l) => l.serviceCharged), rates.serviceChargeMilli)
  const taxBase = sum((l) => l.taxable) + (rates.serviceChargeTaxed ? serviceChargeCents : 0)
  const taxCents = percentOf(taxBase, rates.taxMilli)
  return {
    lines: totals.map((t) => ({ key: t.line.key, totalCents: t.totalCents })),
    subtotalCents,
    serviceChargeCents,
    taxCents,
    totalCents: subtotalCents + serviceChargeCents + taxCents,
  }
}
